/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

import { globalRef, noop, type Unsubscribe } from '@amazon/vinyl-util'
import { documentEvents } from '@/util/interaction'

interface KeyboardLockApi {
    lock(keyCodes?: string[]): Promise<void>
    unlock(): void
}

const lockState = globalRef(() => ({ locked: false }))

/** Whether the browser is holding Escape for the app, so it no longer exits full screen. */
export function isEscapeLocked(): boolean {
    return lockState.value.locked
}

function keyboardLock(): KeyboardLockApi | null {
    const keyboard = (navigator as { keyboard?: Partial<KeyboardLockApi> }).keyboard
    return typeof keyboard?.lock === 'function' && typeof keyboard.unlock === 'function'
        ? (keyboard as KeyboardLockApi)
        : null
}

/** While the document is full screen, delivers Escape to the app (holding it still exits). */
export function bindFullscreenEscapeLock(): Unsubscribe {
    const sync = () => {
        const api = keyboardLock()
        if (!api) return
        if (document.fullscreenElement) {
            api.lock(['Escape'])
                .then(() => {
                    lockState.value.locked = document.fullscreenElement != null
                    if (!lockState.value.locked) api.unlock()
                })
                .catch(noop)
        } else {
            lockState.value.locked = false
            api.unlock()
        }
    }
    sync()
    const stop = documentEvents.on('fullscreenchange', sync)
    return () => {
        stop()
        lockState.value.locked = false
        keyboardLock()?.unlock()
    }
}
